import { ChangeEvent, useEffect, useState } from "react";
import { ExternalLink, FileUp, PenSquare, ShoppingCart } from "lucide-react";
import { Link } from "react-router";
import { useAuth } from "../contexts/AuthContext";
import { PasswordChangePrompt } from "../components/PasswordChangePrompt";
import {
  fetchCustomerProject,
  fetchCustomerProjects,
  ProjectDetail,
  submitProjectAssets,
  submitProjectRevision,
  unlockPublicDraft,
  WorkflowApiError,
} from "../services/workflow";
import {
  formatCurrency,
  formatDateTime,
  getProjectStatusMeta,
  getStripePaymentStatusLabel,
} from "../services/workflow-ui";

interface UploadFilePayload {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  base64Data: string;
}

const CUSTOMER_UPLOAD_ALLOWED_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".webp",
  ".gif",
  ".avif",
  ".heic",
  ".heif",
  ".pdf",
];
const CUSTOMER_UPLOAD_ALLOWED_FORMATS_TEXT = "JPG/JPEG, PNG, WEBP, GIF, AVIF, HEIC/HEIF und PDF";
const CUSTOMER_UPLOAD_ACCEPT = CUSTOMER_UPLOAD_ALLOWED_EXTENSIONS.join(",");

function getFileExtension(fileName: string) {
  const extensionIndex = fileName.lastIndexOf(".");
  return extensionIndex >= 0 ? fileName.slice(extensionIndex).toLowerCase() : "";
}

function isAllowedCustomerUploadFile(file: File) {
  return CUSTOMER_UPLOAD_ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
}

function getAssetUploadErrorMessage(error: unknown) {
  if (!(error instanceof WorkflowApiError)) {
    return "Uploads konnten gerade nicht übermittelt werden. Bitte versuchen Sie es erneut.";
  }

  if (error.status === 413) {
    return "Die ausgewählten Dateien sind zusammen zu groß. Bitte laden Sie kleinere oder weniger Dateien gleichzeitig hoch.";
  }

  if (error.status && error.status >= 500) {
    return "Die Dateien konnten gerade serverseitig nicht verarbeitet werden. Bitte versuchen Sie es in wenigen Minuten erneut.";
  }

  return error.message;
}

function readFileAsBase64(file: File) {
  return new Promise<UploadFilePayload>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Datei konnte nicht gelesen werden"));
        return;
      }

      const [, base64Data = ""] = result.split(",", 2);
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        base64Data,
      });
    };

    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsDataURL(file);
  });
}

type DashboardActionType = "draft_review" | "assets_upload" | null;

interface DashboardStatusContent {
  label: string;
  description: string;
  helper: string;
}

interface TimelineEntry {
  title: string;
  date: string | null;
  description: string;
  link?: string;
}

function getDashboardStatusContent(project: ProjectDetail): DashboardStatusContent {
  switch (project.status) {
    case "questionnaire_received":
      return {
        label: "In Erstellung",
        description:
          "Ihre Angaben sind bei uns eingegangen und werden für die Entwurfserstellung vorbereitet.",
        helper: "Aktuell ist kein Schritt von Ihnen nötig.",
      };
    case "draft_in_progress":
      return {
        label: "In Erstellung",
        description: "Ihr Entwurf wird gerade ausgearbeitet.",
        helper: "Aktuell ist kein Schritt von Ihnen nötig.",
      };
    case "draft_ready":
      return {
        label: "In Vorbereitung",
        description: "Ihr Entwurf ist intern fertig und wird für die Freigabe vorbereitet.",
        helper: "Aktuell ist kein Schritt von Ihnen nötig.",
      };
    case "draft_sent":
      return {
        label: "Entwurf fertig",
        description:
          "Ihr Entwurf ist bereit. Bitte prüfen Sie ihn jetzt und bestätigen Sie ihn, wenn alles passt.",
        helper: "Ihr nächster Schritt ist der Kauf auf Basis des Entwurfs.",
      };
    case "revision_requested":
      return {
        label: "In Überarbeitung",
        description: "Ihre Änderungswünsche liegen vor und werden aktuell eingearbeitet.",
        helper: "Aktuell ist kein weiterer Schritt von Ihnen nötig.",
      };
    case "ordered":
      return {
        label: "Beauftragt",
        description: "Der Entwurf wurde bestätigt und die Umsetzung ist gestartet.",
        helper: "Falls noch Inhalte fehlen, können Sie diese jetzt übermitteln.",
      };
    case "assets_pending":
      return {
        label: "Wartet auf Ihre Daten",
        description:
          "Der Auftrag ist bestätigt. Für die Finalisierung brauchen wir noch Inhalte, Dateien oder Zugangsdaten.",
        helper: "Bitte übermitteln Sie jetzt die benötigten Daten.",
      };
    case "finalizing":
      return {
        label: "In Umsetzung",
        description: "Ihre Website wird final umgesetzt und für den Go-live vorbereitet.",
        helper: "Falls noch Inhalte, Bilder oder Zugangsdaten fehlen, können Sie diese weiterhin übermitteln.",
      };
    case "live":
      return {
        label: "Online",
        description: "Ihre Website ist veröffentlicht und online erreichbar.",
        helper: "Aktuell ist kein weiterer Schritt von Ihnen nötig.",
      };
    default:
      return {
        label: getProjectStatusMeta(project.status).label,
        description: getProjectStatusMeta(project.status).description,
        helper: "Aktuell ist kein weiterer Schritt von Ihnen nötig.",
      };
  }
}

function getDashboardActionType(project: ProjectDetail): DashboardActionType {
  if (project.status === "draft_sent" && project.currentDraft && !project.order) {
    return "draft_review";
  }

  if (
    project.status === "assets_pending" ||
    project.status === "ordered" ||
    project.status === "finalizing"
  ) {
    return "assets_upload";
  }

  return null;
}

function getFirstAssetSubmittedAt(project: ProjectDetail) {
  if (project.assets.length === 0) {
    return null;
  }

  return [...project.assets]
    .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime())[0]
    ?.createdAt;
}

function buildTimeline(project: ProjectDetail): TimelineEntry[] {
  const entries: TimelineEntry[] = [
    {
      title: "Anfrage eingegangen",
      date: project.questionnaire.createdAt,
      description: "Ihr Fragebogen wurde erfolgreich übermittelt.",
    },
  ];

  if (project.currentDraft?.sentAt) {
    entries.push({
      title: "Entwurf bereitgestellt",
      date: project.currentDraft.sentAt,
      description: `Version ${project.currentDraft.version} wurde für Ihre Prüfung freigegeben.`,
    });
  }

  const revisions = [...project.revisionsList].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  );

  revisions.forEach((revision) => {
    entries.push({
      title: `Revision ${revision.roundNumber} angefragt`,
      date: revision.createdAt,
      description: revision.feedback,
    });
  });

  if (project.order) {
    entries.push({
      title: "Bestellung abgeschlossen",
      date: project.order.acceptedAt,
      description: `Zahlungspflichtig bestellt durch ${project.order.acceptedByName}.`,
    });
  }

  const firstAssetSubmittedAt = getFirstAssetSubmittedAt(project);
  if (firstAssetSubmittedAt) {
    entries.push({
      title: "Daten übermittelt",
      date: firstAssetSubmittedAt,
      description: `${project.assetCount} Datei- oder Daten-Einträge wurden übermittelt.`,
    });
  }

  if (project.status === "live" && project.liveUrl) {
    entries.push({
      title: "Website online",
      date: project.updatedAt,
      description: "Die Website ist veröffentlicht.",
      link: project.liveUrl,
    });
  }

  return entries.sort((left, right) => {
    if (!left.date && !right.date) {
      return 0;
    }

    if (!left.date) {
      return 1;
    }

    if (!right.date) {
      return -1;
    }

    return new Date(right.date).getTime() - new Date(left.date).getTime();
  });
}

export function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ProjectDetail[]>([]);
  const [activeProjectId, setActiveProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [revisionFeedback, setRevisionFeedback] = useState("");
  const [assetForm, setAssetForm] = useState({
    serverAccessNotes: "",
    generalNotes: "",
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmittingRevision, setIsSubmittingRevision] = useState(false);
  const [isSubmittingAssets, setIsSubmittingAssets] = useState(false);
  const [previewFrameUrl, setPreviewFrameUrl] = useState("");
  const [isLoadingPreviewFrame, setIsLoadingPreviewFrame] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      try {
        const projectSummaries = await fetchCustomerProjects();
        const detailedProjects = await Promise.all(
          projectSummaries.map((project) => fetchCustomerProject(project.id)),
        );

        if (!isMounted) {
          return;
        }

        setProjects(detailedProjects);
        setActiveProjectId(detailedProjects[0]?.id || "");
      } catch (loadError) {
        if (isMounted) {
          setError(
            loadError instanceof WorkflowApiError
              ? loadError.message
              : "Projektbereich konnte nicht geladen werden",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  const activeProject = projects.find((project) => project.id === activeProjectId) || projects[0] || null;
  const isImpersonatingCustomerView = user?.impersonation?.active === true;
  const isReadOnlyCustomerView =
    isImpersonatingCustomerView && user?.impersonation?.readOnly !== false;
  const canSubmitAssets = !isReadOnlyCustomerView || isImpersonatingCustomerView;
  const passwordChangeNotice = user?.mustChangePassword ? (
    isReadOnlyCustomerView ? (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <h2 className="text-xl font-semibold text-gray-900">Bitte verwenden Sie ein eigenes Passwort</h2>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          Dieses Kundenkonto nutzt aktuell noch das bereitgestellte Portal-Passwort. In der
          Admin-Kundenansicht bleibt die Passwortänderung aus Sicherheitsgründen deaktiviert.
        </p>
      </section>
    ) : (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <PasswordChangePrompt
          title="Bitte verwenden Sie ein eigenes Passwort"
          description="Ihr Konto nutzt aktuell noch das von uns bereitgestellte Portal-Passwort. Bitte vergeben Sie aus Sicherheitsgründen jetzt Ihr eigenes Passwort."
          submitLabel="Eigenes Passwort speichern"
          onSuccess={() => {
            setSuccessMessage("Ihr eigenes Passwort wurde gespeichert.");
            setError("");
          }}
        />
      </section>
    )
  ) : null;

  useEffect(() => {
    let isMounted = true;

    if (!activeProject?.currentDraft || !activeProject.currentDraft.sentAt) {
      setPreviewFrameUrl("");
      setIsLoadingPreviewFrame(false);
      return () => undefined;
    }

    setIsLoadingPreviewFrame(true);
    setPreviewFrameUrl("");

    unlockPublicDraft(
      activeProject.currentDraft.shareSlug,
      activeProject.currentDraft.previewPassword,
    )
      .then((draft) => {
        if (isMounted) {
          setPreviewFrameUrl(draft.previewUrl || activeProject.currentDraft?.previewUrl || "");
        }
      })
      .catch(() => {
        if (isMounted) {
          setPreviewFrameUrl("");
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingPreviewFrame(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeProject?.currentDraft?.id, activeProject?.currentDraft?.previewPassword, activeProject?.currentDraft?.sentAt]);

  const replaceProject = (nextProject: ProjectDetail, message: string) => {
    setProjects((currentProjects) =>
      currentProjects.map((project) => (project.id === nextProject.id ? nextProject : project)),
    );
    setActiveProjectId(nextProject.id);
    setSuccessMessage(message);
    setError("");
  };

  const handleRevisionSubmit = async () => {
    if (!activeProject || isReadOnlyCustomerView) {
      return;
    }

    setIsSubmittingRevision(true);
    setSuccessMessage("");
    setError("");

    try {
      const nextProject = await submitProjectRevision(activeProject.id, revisionFeedback);
      replaceProject(nextProject, "Revision erfolgreich eingereicht");
      setRevisionFeedback("");
    } catch (submitError) {
      setError(
        submitError instanceof WorkflowApiError
          ? submitError.message
          : "Revision konnte nicht übermittelt werden",
      );
    } finally {
      setIsSubmittingRevision(false);
    }
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files || []);
    const validFiles = nextFiles.filter((file) => isAllowedCustomerUploadFile(file));
    const invalidFiles = nextFiles.filter((file) => !isAllowedCustomerUploadFile(file));

    setSelectedFiles(validFiles);

    if (invalidFiles.length > 0) {
      setSuccessMessage("");
      setError(
        `Diese Dateien sind nicht erlaubt: ${invalidFiles
          .map((file) => file.name)
          .join(", ")}. Bitte nur ${CUSTOMER_UPLOAD_ALLOWED_FORMATS_TEXT} hochladen.`,
      );
      return;
    }

    setError("");
  };

  const handleAssetsSubmit = async () => {
    if (!activeProject || !canSubmitAssets) {
      return;
    }

    if (
      selectedFiles.length === 0 &&
      !assetForm.serverAccessNotes.trim() &&
      !assetForm.generalNotes.trim()
    ) {
      setSuccessMessage("");
      setError("Bitte laden Sie mindestens eine Datei hoch oder ergänzen Sie optionale Hinweise.");
      return;
    }

    setIsSubmittingAssets(true);
    setSuccessMessage("");
    setError("");

    try {
      const assets = await Promise.all(selectedFiles.map((file) => readFileAsBase64(file)));
      const nextProject = await submitProjectAssets(activeProject.id, {
        ...assetForm,
        assets,
      });
      replaceProject(nextProject, "Dateien und Zugangsdaten wurden übermittelt");
      setAssetForm({
        serverAccessNotes: "",
        generalNotes: "",
      });
      setSelectedFiles([]);
    } catch (submitError) {
      setError(getAssetUploadErrorMessage(submitError));
    } finally {
      setIsSubmittingAssets(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-600">Projektbereich wird geladen...</div>;
  }

  if (!activeProject) {
    return (
      <div className="space-y-4">
        {passwordChangeNotice}
        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}
        <div className="rounded-2xl border border-dashed border-gray-300 bg-slate-50 px-6 py-10 text-sm text-gray-700">
          Aktuell ist noch kein Projekt Ihrem Kundenkonto zugeordnet.
        </div>
      </div>
    );
  }

  const statusMeta = getProjectStatusMeta(activeProject.status);
  const dashboardStatus = getDashboardStatusContent(activeProject);
  const actionType = getDashboardActionType(activeProject);
  const timeline = buildTimeline(activeProject);
  const hasSubmittedAssets = activeProject.assetCount > 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {passwordChangeNotice}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Ihr Dashboard</h1>
          <p className="text-gray-700">Hier sehen Sie den aktuellen Stand Ihres Projekts auf einen Blick.</p>
        </div>

        {projects.length > 1 ? (
          <div className="flex items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm">
            <span>{projects.length} Projekte</span>
            <select
              value={activeProject.id}
              onChange={(event) => setActiveProjectId(event.target.value)}
              className="max-w-[220px] truncate bg-transparent text-sm text-gray-950 focus:outline-none"
              aria-label="Projekt auswählen"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      ) : null}

      {activeProject.paymentIssue ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {activeProject.paymentIssue.message}
          {activeProject.paymentIssue.paymentStatus ? (
            <span className="mt-1 block font-semibold">
              Stripe-Status: {getStripePaymentStatusLabel(activeProject.paymentIssue.paymentStatus)}
            </span>
          ) : null}
        </div>
      ) : null}

      {activeProject.order && !activeProject.order.isPaymentConfirmed ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Ihr Auftrag wurde bereits übernommen. Die SEPA-Zahlung ist aber noch nicht endgültig auf
          unserem Konto eingegangen.
          {activeProject.order.paymentStatus ? (
            <span className="mt-1 block font-semibold">
              Stripe-Status: {getStripePaymentStatusLabel(activeProject.order.paymentStatus)}
            </span>
          ) : null}
        </div>
      ) : null}

      {isReadOnlyCustomerView ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Diese Kundenansicht ist weitgehend schreibgeschützt. Bestellungen und Revisionen bleiben
          deaktiviert. Uploads sind für Tests weiterhin möglich.
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_360px]">
        <div className="flex h-full flex-col rounded-2xl border border-gray-300 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">Projekt</p>
              <h3 className="mt-1 truncate text-lg font-semibold text-gray-900">{activeProject.title}</h3>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">Status</p>
              <span
                className={`mt-1 inline-flex rounded-full border border-black/10 px-3 py-1 text-sm font-semibold shadow-sm ${statusMeta.className}`}
              >
                {dashboardStatus.label}
              </span>
            </div>
          </div>

          <div className="mt-4 flex flex-1 flex-col border-t border-gray-300 pt-4">
            <div className="min-w-0 rounded-2xl bg-slate-100 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">Status</p>
              <p className="mt-2 text-base font-medium leading-relaxed text-gray-900">
                {dashboardStatus.description}
              </p>
            </div>

            <div className="mt-4">
              <div className="rounded-xl border border-gray-300 bg-white px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">Nächster Schritt</p>
                <p className="mt-2 text-sm font-medium text-gray-900">{dashboardStatus.helper}</p>
              </div>
            </div>
          </div>
        </div>

        {activeProject.currentDraft ? (
          <div className="overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-sm">
            <a
              href={`/preview/${activeProject.currentDraft.shareSlug}`}
              target="_blank"
              rel="noreferrer"
              className="block transition hover:bg-slate-100/80"
            >
              <div className="relative aspect-[16/9] bg-slate-100">
                <div className="absolute right-3 top-3 z-10 rounded-full border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm">
                  <ExternalLink size={16} />
                </div>
                {previewFrameUrl ? (
                  <div className="h-full overflow-hidden bg-white">
                    <iframe
                      title={`Vorschau von ${activeProject.title}`}
                      src={previewFrameUrl}
                      className="pointer-events-none border-0"
                      style={{
                        width: "333.333%",
                        height: "333.333%",
                        transform: "scale(0.3)",
                        transformOrigin: "top left",
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-full flex-col bg-white">
                    <div className="flex items-center gap-2 border-b border-gray-300 px-4 py-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
                      <span className="h-2.5 w-2.5 rounded-full bg-gray-400" />
                    </div>
                    <div className="flex flex-1 items-center justify-center px-4 text-center text-sm text-gray-600">
                      {isLoadingPreviewFrame
                        ? "Vorschau wird geladen..."
                        : "Noch keine Vorschau verfügbar"}
                    </div>
                  </div>
                )}
              </div>
            </a>

            <div className="grid grid-cols-2 gap-3 border-t border-gray-300 px-4 py-3 text-sm">
              <div>
                <p className="font-medium text-gray-700">Version</p>
                <p className="mt-1 font-medium text-gray-900">V{activeProject.currentDraft.version}</p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Passwort</p>
                <p className="mt-1 font-mono text-gray-900">{activeProject.currentDraft.previewPassword}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-gray-400 bg-slate-50 px-6 py-10 text-sm text-gray-700">
            Sobald ein Entwurf verfügbar ist, erscheint hier die Vorschau.
          </div>
        )}
      </section>

      {actionType === "draft_review" ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Nächster Schritt</h2>
            <p className="text-sm text-gray-700">
              Der Kaufabschluss erfolgt jetzt direkt über eine separate gehostete Stripe-Kaufseite.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div className="rounded-3xl border border-gray-300 bg-white p-6 shadow-sm space-y-5">
              <div className="flex items-center gap-2">
                <ShoppingCart size={18} />
                <h3 className="text-xl font-semibold text-gray-950">Kauf über separate Unterseite</h3>
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm leading-relaxed text-blue-900">
                Der eigentliche Kaufabschluss findet nicht mehr direkt im Dashboard statt. Stattdessen
                öffnen Sie eine eigene Kaufseite, die Sie direkt in das gehostete Stripe-Checkout
                weiterleitet.
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                    Freigegebener Entwurf
                  </p>
                  <p className="mt-2 text-base font-semibold text-gray-950">
                    Version {activeProject.currentDraft?.version}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-600">
                    Preisstruktur
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-800">
                    {formatCurrency(activeProject.pricing.setup)} einmalig +{" "}
                    {formatCurrency(activeProject.pricing.monthly)} pro Monat, jeweils
                    zzgl. gesetzlicher MwSt.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-300 bg-slate-50 p-4 text-sm leading-relaxed text-gray-700">
                Auf der Kaufseite sehen Sie noch einmal die Konditionen und wechseln dann direkt auf
                das Stripe-Formular für Zahlung, Rechnungsadresse und Steuerdaten. Die
                gesetzliche MwSt. wird dort zusätzlich ausgewiesen.
              </div>

              {isReadOnlyCustomerView ? (
                <span className="inline-flex items-center justify-center rounded-2xl bg-gray-300 px-5 py-3 text-sm font-semibold text-gray-700">
                  Direkt zu Stripe
                </span>
              ) : (
                <Link
                  to={`/dashboard/kauf/${activeProject.id}?autostart=1`}
                  className="inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                >
                  Direkt zu Stripe
                </Link>
              )}
            </div>

            <div className="rounded-2xl border border-gray-300 bg-white p-6 shadow-sm space-y-4">
              <div className="flex items-center gap-2">
                <PenSquare size={20} />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Revision anfordern</h3>
                  <p className="text-sm text-gray-700">
                    Nur wenn noch konkrete Änderungen nötig sind.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-300 bg-slate-100 p-4 text-sm text-slate-800">
                Maximal {activeProject.revisions.included} Durchläufe, aktuell verbleibend:{" "}
                {activeProject.revisions.remaining}.
              </div>

              <textarea
                value={revisionFeedback}
                onChange={(event) => setRevisionFeedback(event.target.value)}
                rows={8}
                disabled={isReadOnlyCustomerView}
                className="w-full rounded-xl border border-gray-400 px-4 py-3 text-gray-950 placeholder:text-gray-500 focus:border-black focus:outline-none"
                placeholder="Bitte beschreiben Sie Ihre Änderungswünsche möglichst konkret."
              />

              <button
                type="button"
                onClick={handleRevisionSubmit}
                disabled={
                  isReadOnlyCustomerView ||
                  isSubmittingRevision ||
                  activeProject.revisions.remaining <= 0
                }
                className="rounded-lg border border-gray-400 px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmittingRevision ? "Wird übermittelt..." : "Revision absenden"}
              </button>
            </div>
          </div>
        </section>
      ) : null}

      {actionType === "assets_upload" ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Nächster Schritt</h2>
            <p className="text-sm text-gray-700">
              {hasSubmittedAssets
                ? "Weitere Inhalte, Bilder oder Zugangsdaten können Sie hier jederzeit ergänzen."
                : "Bitte übermitteln Sie jetzt Inhalte und Zugänge."}
            </p>
          </div>

          <div className="rounded-2xl border border-gray-300 bg-white p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <FileUp size={20} />
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Daten & Dateien übermitteln</h3>
                <p className="text-sm text-gray-700">
                  Zugangsdaten, Bilder, Logos oder sonstige Inhalte für die Finalisierung. Bereits
                  gesendete Daten können hier jederzeit ergänzt werden.
                </p>
              </div>
            </div>

            <label className="space-y-2 text-sm text-gray-800">
              <span>Serverzugang / DNS / bestehende Systeme (optional)</span>
              <textarea
                value={assetForm.serverAccessNotes}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    serverAccessNotes: event.target.value,
                  }))
                }
                rows={5}
                disabled={!canSubmitAssets}
                className="w-full rounded-xl border border-gray-400 bg-white px-4 py-3 text-gray-950 placeholder:text-gray-500 focus:border-black focus:outline-none"
                placeholder="z. B. Hosting-Anbieter, Domain-Zugang, CMS-Zugang, E-Mail-Zugang"
              />
            </label>

            <label className="space-y-2 text-sm text-gray-800">
              <span>Weitere Hinweise (optional)</span>
              <textarea
                value={assetForm.generalNotes}
                onChange={(event) =>
                  setAssetForm((current) => ({
                    ...current,
                    generalNotes: event.target.value,
                  }))
                }
                rows={4}
                disabled={!canSubmitAssets}
                className="w-full rounded-xl border border-gray-400 bg-white px-4 py-3 text-gray-950 placeholder:text-gray-500 focus:border-black focus:outline-none"
                placeholder="z. B. Bildwunsch, Reihenfolge, Inhalte, Ansprechpartner"
              />
            </label>

            <label className="space-y-2 text-sm text-gray-800">
              <span>Dateien hochladen</span>
              <input
                type="file"
                multiple
                accept={CUSTOMER_UPLOAD_ACCEPT}
                onChange={handleFilesChange}
                disabled={!canSubmitAssets}
                className="w-full rounded-xl border border-gray-400 bg-white px-4 py-2.5 text-gray-950"
              />
              <p className="text-xs text-gray-600">
                Bilder und Dateien können auch ohne zusätzliche Nachricht gesendet werden. Erlaubt
                sind nur {CUSTOMER_UPLOAD_ALLOWED_FORMATS_TEXT}. Die Dateien werden beim Upload
                sicherheitsgeprüft.
              </p>
            </label>

            {selectedFiles.length > 0 ? (
              <div className="rounded-xl border border-slate-300 bg-slate-100 p-4 text-sm text-slate-800">
                {selectedFiles.map((file) => file.name).join(", ")}
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleAssetsSubmit}
              disabled={!canSubmitAssets || isSubmittingAssets}
              className="mt-3 rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmittingAssets ? "Wird übermittelt..." : "Dateien und optionale Hinweise senden"}
            </button>
          </div>
        </section>
      ) : null}

      <section className="space-y-3">
        <div className="pl-1">
          <h2 className="text-xl font-semibold text-gray-900">Verlauf</h2>
          <p className="text-sm text-gray-700">Alle wichtigen Stationen in zeitlicher Reihenfolge.</p>
        </div>

        <div className="rounded-2xl border border-gray-300 bg-white p-6 shadow-sm">
          <div className="space-y-3">
            {timeline.map((entry, index) => (
              <div
                key={`${entry.title}-${entry.date || index}`}
                className={`rounded-xl px-4 py-4 ${
                  index === 0
                    ? "border border-blue-300 bg-blue-50"
                    : "border border-gray-300 bg-white"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{entry.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-700">{entry.description}</p>
                  </div>
                  {entry.date ? (
                    <p className="text-sm font-semibold text-gray-900">{formatDateTime(entry.date)}</p>
                  ) : null}
                </div>

                {entry.link ? (
                  <a
                    href={entry.link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-block text-sm font-medium text-black underline"
                  >
                    {entry.link}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
